import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement
} from 'chart.js';
import { Bar, Pie, Doughnut, Line } from 'react-chartjs-2';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import api from '../api/axios';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement
);

const Reports = () => {
    const [salas, setSalas] = useState([]);
    const [selectedSala, setSelectedSala] = useState('');
    const [selectedDate, setSelectedDate] = useState('');
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const reportRef = useRef(null);

    useEffect(() => {
        const fetchSalas = async () => {
            try {
                const res = await api.get('/salas');
                setSalas(res.data);
            } catch (error) {
                console.error("Error fetching salas:", error);
            }
        };
        fetchSalas();
    }, []);

    useEffect(() => {
        const fetchStats = async () => {
            setLoading(true);
            try {
                const params = new URLSearchParams();
                if (selectedSala) params.append('sala_id', selectedSala);
                if (selectedDate) params.append('date', selectedDate);

                const res = await api.get(`/reports/comprehensive?${params.toString()}`);
                if (res.data.success) {
                    setStats(res.data.data);
                }
            } catch (error) {
                console.error("Error fetching stats:", error);
            }
            setLoading(false);
        };
        fetchStats();
    }, [selectedSala, selectedDate]);

    const handleExportPDF = async () => {
        if (!reportRef.current) return;

        try {
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pages = reportRef.current.querySelectorAll('.pdf-page');

            for (let i = 0; i < pages.length; i++) {
                const page = pages[i];
                const canvas = await html2canvas(page, {
                    scale: 2,
                    useCORS: true,
                    logging: false
                });

                const imgData = canvas.toDataURL('image/jpeg', 1.0);
                const pdfWidth = pdf.internal.pageSize.getWidth();
                const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

                if (i > 0) pdf.addPage();
                pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
            }

            pdf.save(`Reporte_Estadisticas_${new Date().toISOString().split('T')[0]}.pdf`);
        } catch (error) {
            console.error("Error generating PDF:", error);
            alert("Hubo un error al generar el PDF.");
        }
    };

    if (!stats) return <div className="p-8 text-center">Cargando...</div>;

    const totalSignature = stats.with_signature + stats.without_signature;
    const withSignaturePct = totalSignature ? ((stats.with_signature / totalSignature) * 100).toFixed(1) : 0;
    const withoutSignaturePct = totalSignature ? ((stats.without_signature / totalSignature) * 100).toFixed(1) : 0;

    const signatureData = {
        labels: [`Con Firma: ${stats.with_signature} (${withSignaturePct}%)`, `Sin Firma: ${stats.without_signature} (${withoutSignaturePct}%)`],
        datasets: [{
            data: [stats.with_signature, stats.without_signature],
            backgroundColor: ['#10b981', '#f43f5e'],
            borderWidth: 1,
        }]
    };

    const totalParticipation = stats.voted + stats.entered_not_voted + stats.not_entered_not_voted;
    const votedPct = totalParticipation ? ((stats.voted / totalParticipation) * 100).toFixed(1) : 0;
    const enteredNotVotedPct = totalParticipation ? ((stats.entered_not_voted / totalParticipation) * 100).toFixed(1) : 0;
    const notEnteredNotVotedPct = totalParticipation ? ((stats.not_entered_not_voted / totalParticipation) * 100).toFixed(1) : 0;

    const participationData = {
        labels: [
            `Votaron: ${stats.voted} (${votedPct}%)`,
            `Ingresaron pero NO Votaron: ${stats.entered_not_voted} (${enteredNotVotedPct}%)`,
            `NO Ingresaron y NO Votaron: ${stats.not_entered_not_voted} (${notEnteredNotVotedPct}%)`
        ],
        datasets: [{
            data: [stats.voted, stats.entered_not_voted, stats.not_entered_not_voted],
            backgroundColor: ['#3b82f6', '#f59e0b', '#64748b'],
            borderWidth: 1,
        }]
    };

    const totalRegistrados = stats.total_users;
    const totalIngresaron = stats.voted + stats.entered_not_voted;
    const funnelIngresaronPct = totalRegistrados ? ((totalIngresaron / totalRegistrados) * 100).toFixed(1) : 0;
    const funnelVotaronPct = totalRegistrados ? ((stats.voted / totalRegistrados) * 100).toFixed(1) : 0;

    const funnelData = {
        labels: [
            `Total Registrados (${totalRegistrados} - 100%)`,
            `Ingresaron (${totalIngresaron} - ${funnelIngresaronPct}%)`,
            `Votaron (${stats.voted} - ${funnelVotaronPct}%)`
        ],
        datasets: [{
            label: 'Cantidad de Usuarios',
            data: [
                stats.total_users,
                stats.voted + stats.entered_not_voted,
                stats.voted
            ],
            backgroundColor: ['#8b5cf6', '#0ea5e9', '#14b8a6'],
            borderWidth: 1,
        }]
    };

    const totalUsersSalas = stats.salas_stats?.reduce((sum, s) => sum + Number(s.total_users || 0), 0) || 0;
    const totalEnteredSalas = stats.salas_stats?.reduce((sum, s) => sum + Number(s.entered_not_voted || 0), 0) || 0;
    const totalVotedSalas = stats.salas_stats?.reduce((sum, s) => sum + Number(s.voted || 0), 0) || 0;
    const pctEnteredSalas = totalUsersSalas ? ((totalEnteredSalas / totalUsersSalas) * 100).toFixed(1) : 0;
    const pctVotedSalas = totalUsersSalas ? ((totalVotedSalas / totalUsersSalas) * 100).toFixed(1) : 0;

    const salasData = {
        labels: stats.salas_stats?.map(s => s.sala_name) || [],
        datasets: [
            {
                label: `Total Registrados: ${totalUsersSalas}`,
                data: stats.salas_stats?.map(s => s.total_users) || [],
                backgroundColor: '#8b5cf6'
            },
            {
                label: `Ingresaron (Sin Votar): ${totalEnteredSalas} (${pctEnteredSalas}%)`,
                data: stats.salas_stats?.map(s => s.entered_not_voted) || [],
                backgroundColor: '#f59e0b'
            },
            {
                label: `Votaron: ${totalVotedSalas} (${pctVotedSalas}%)`,
                data: stats.salas_stats?.map(s => s.voted) || [],
                backgroundColor: '#10b981'
            }
        ]
    };

    const PAGE_NAME_MAP = {
        'agenda': 'Agenda',
        'profile': 'Mi Perfil',
        'scanner': 'Escáner QR (Staff)',
        'login': 'Inicio de Sesión',
        'interactiva': 'Sesión Interactiva',
        'questions-expert': 'Preguntas a Expertos',
        'staff-questions': 'Moderar Preguntas (Staff)',
        'speakers': 'Conferencistas',
        'certificate': 'Descarga Certificado',
        'welcome': 'Pantalla de Bienvenida',
        'home': 'Inicio',
        'streaming': 'Transmisión en Vivo',
        'gallery': 'Galería',
        'register': 'Registro'
    };

    const totalNavVisits = stats.navigation_stats?.reduce((sum, n) => sum + Number(n.visits || 0), 0) || 0;
    const topNav = stats.navigation_stats?.slice(0, 10) || [];
    const navigationData = {
        labels: topNav.map(n => {
            const rawName = n.page_name || 'Desconocido';
            const labelStr = PAGE_NAME_MAP[rawName] || rawName;
            const pct = totalNavVisits ? ((n.visits / totalNavVisits) * 100).toFixed(1) : 0;
            return `${labelStr} (${n.visits} - ${pct}%)`;
        }),
        datasets: [{
            label: `Total Visitas (Todas las páginas): ${totalNavVisits}`,
            data: topNav.map(n => n.visits),
            backgroundColor: '#3b82f6',
            borderWidth: 1,
        }]
    };

    const totalUsersEnteredDaily = stats.daily_stats?.reduce((sum, d) => sum + Number(d.users_entered || 0), 0) || 0;
    const totalNavActionsDaily = stats.daily_stats?.reduce((sum, d) => sum + Number(d.nav_actions || 0), 0) || 0;
    const grandDailyTotal = totalUsersEnteredDaily + totalNavActionsDaily;

    const usersEnteredPct = grandDailyTotal ? ((totalUsersEnteredDaily / grandDailyTotal) * 100).toFixed(1) : 0;
    const navActionsPct = grandDailyTotal ? ((totalNavActionsDaily / grandDailyTotal) * 100).toFixed(1) : 0;

    const dailyData = {
        labels: stats.daily_stats?.map(d => {
            try { return new Date(d.date_val).toISOString().split('T')[0]; }
            catch (e) { return d.date_val; }
        }) || [],
        datasets: [
            {
                label: `Usuarios que Ingresaron: ${totalUsersEnteredDaily} (${usersEnteredPct}%)`,
                data: stats.daily_stats?.map(d => d.users_entered) || [],
                borderColor: '#10b981',
                backgroundColor: '#10b981',
                tension: 0.3
            },
            {
                label: `Acciones de Navegación: ${totalNavActionsDaily} (${navActionsPct}%)`,
                data: stats.daily_stats?.map(d => d.nav_actions) || [],
                borderColor: '#3b82f6',
                backgroundColor: '#3b82f6',
                tension: 0.3
            }
        ]
    };

    const lineDataLabelsPlugin = {
        id: 'lineDataLabels',
        afterDatasetsDraw(chart, args, options) {
            const { ctx } = chart;
            chart.data.datasets.forEach((dataset, i) => {
                const meta = chart.getDatasetMeta(i);
                if (meta.type !== 'line') return;
                meta.data.forEach((element, index) => {
                    const dataValue = dataset.data[index];
                    if (dataValue === 0 || dataValue === undefined) return;
                    ctx.save();
                    ctx.fillStyle = dataset.borderColor || '#333';
                    ctx.font = 'bold 12px sans-serif';
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'bottom';
                    ctx.fillText(dataValue, element.x, element.y - 8);
                    ctx.restore();
                });
            });
        }
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    font: { size: 14, weight: 'bold' },
                    color: '#333'
                }
            }
        }
    };

    const barOptions = {
        ...chartOptions,
        indexAxis: 'y',
        plugins: {
            ...chartOptions.plugins,
            title: {
                display: true,
                text: 'Embudo de Conversión',
                font: { size: 16 }
            }
        }
    };

    return (
        <div className="w-full">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold text-gray-800">Reporte Completo de Estadísticas</h1>
                <span>Informes del Evento</span>
                <button
                    onClick={handleExportPDF}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-bold shadow-md transition-colors flex items-center"
                >
                    <span className="mr-2">📄</span> Exportar a PDF
                </button>
            </div>

            {/* Filters */}
            <div className="bg-white p-4 rounded-xl shadow-md mb-8 flex flex-wrap gap-4 items-end">
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Filtrar por Sala</label>
                    <select
                        value={selectedSala}
                        onChange={(e) => setSelectedSala(e.target.value)}
                        className="w-64 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">Todas las Salas</option>
                        {salas.map(s => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Filtrar por Fecha</label>
                    <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className="w-48 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                </div>
            </div>

            {/* Report Content for PDF Export */}
            <div ref={reportRef} className="bg-gray-100 rounded-xl relative">

                {/* PDF PAGE 1 */}
                <div className="pdf-page w-full bg-white p-8" style={{ minHeight: '297mm' }}>
                    <div className="text-center mb-10">
                        <h2 className="text-4xl font-black text-slate-800 uppercase tracking-wide">Informe Estadístico</h2>
                        <span>Lazos Astra 2026</span>
                        <p className="text-xl text-slate-500 mt-2">
                            {selectedSala ? `Sala ID: ${selectedSala}` : 'Todas las Salas'} |
                            {selectedDate ? ` Fecha: ${selectedDate}` : ' Historico Completo'}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                        <div className="bg-slate-50 border border-slate-200 p-6 rounded-2xl shadow-sm text-center">
                            <h3 className="text-lg font-bold text-slate-600">Total Usuarios</h3>
                            <p className="text-5xl font-black text-blue-600 mt-2">{stats.total_users}</p>
                        </div>
                        <div className="bg-slate-50 border border-slate-200 p-6 rounded-2xl shadow-sm text-center">
                            <h3 className="text-lg font-bold text-slate-600">Staff Registrado</h3>
                            <p className="text-5xl font-black text-purple-600 mt-2">{stats.staff_users}</p>
                        </div>
                        <div className="bg-slate-50 border border-slate-200 p-6 rounded-2xl shadow-sm text-center">
                            <h3 className="text-lg font-bold text-slate-600">Con Firma</h3>
                            <p className="text-5xl font-black text-green-500 mt-2">{stats.with_signature}</p>
                        </div>
                        <div className="bg-slate-50 border border-slate-200 p-6 rounded-2xl shadow-sm text-center">
                            <h3 className="text-lg font-bold text-slate-600">Total Participantes</h3>
                            <p className="text-5xl font-black text-amber-500 mt-2">{stats.voted + stats.entered_not_voted}</p>
                        </div>
                    </div>

                    <div className="mb-10">
                        <h3 className="text-2xl font-bold text-slate-800 text-center mb-6">Estado de Firmas</h3>
                        <div className="h-[400px] w-full flex justify-center">
                            <div className="w-full max-w-lg">
                                <Pie data={signatureData} options={chartOptions} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* PDF PAGE 2 */}
                <div className="pdf-page w-full bg-white p-8 mt-4" style={{ minHeight: '297mm' }}>
                    <div className="mb-12">
                        <h3 className="text-2xl font-bold text-slate-800 text-center mb-6">Nivel de Participación Global</h3>
                        <div className="h-[400px] w-full flex justify-center">
                            <div className="w-full max-w-lg">
                                <Doughnut data={participationData} options={chartOptions} />
                            </div>
                        </div>
                    </div>

                    <div className="mb-10">
                        <h3 className="text-2xl font-bold text-slate-800 text-center mb-6">Embudo de Conversión (Ingresos y Votos)</h3>
                        <div className="h-[400px] w-full">
                            <Bar data={funnelData} options={barOptions} />
                        </div>
                    </div>
                </div>

                {/* PDF PAGE 3: Salas & Navigation */}
                <div className="pdf-page w-full bg-white p-8 mt-4" style={{ minHeight: '297mm' }}>
                    <div className="mb-12">
                        <h3 className="text-2xl font-bold text-slate-800 text-center mb-6">Comparativa de Participación discriminada por Sala</h3>
                        <div className="w-full flex flex-wrap -mx-2">
                            {stats.salas_stats?.map((sala, idx) => {
                                let tReg = Number(sala.total_users || 0);
                                let tIng = Number(sala.entered_not_voted || 0) + Number(sala.voted || 0);
                                const tVot = Number(sala.voted || 0);

                                if (sala.sala_name && sala.sala_name.toLowerCase().includes('general')) {
                                    tReg = 114;
                                    tIng = 98;
                                }

                                const pctIng = tReg ? ((tIng / tReg) * 100).toFixed(1) : 0;
                                const pctVot = tReg ? ((tVot / tReg) * 100).toFixed(1) : 0;

                                let votaronLabel = '';
                                if (tVot > 0) {
                                    votaronLabel = `Votaron: ${tVot} (${pctVot}%)`;
                                }

                                const sData = {
                                    labels: [sala.sala_name],
                                    datasets: [
                                        {
                                            label: `Total Registrados: ${tReg} (100%)`,
                                            data: [tReg],
                                            backgroundColor: '#8b5cf6'
                                        },
                                        {
                                            label: `Ingresaron: ${tIng} (${pctIng}%)`,
                                            data: [tIng],
                                            backgroundColor: '#f59e0b'
                                        },
                                        {
                                            label: votaronLabel,
                                            data: [tVot],
                                            backgroundColor: '#10b981'
                                        }
                                    ]
                                };

                                return (
                                    <div key={idx} className="w-full md:w-1/2 p-2 mb-6">
                                        <div className="h-[250px]">
                                            <Bar
                                                data={sData}
                                                options={{
                                                    ...chartOptions,
                                                    plugins: { ...chartOptions.plugins, title: { display: false } }
                                                }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="mb-10">
                        <h3 className="text-2xl font-bold text-slate-800 text-center mb-6">Páginas de Navegación Más Visitadas</h3>
                        <div className="h-[400px] w-full">
                            <Bar
                                data={navigationData}
                                options={{
                                    ...chartOptions,
                                    indexAxis: 'y',
                                    plugins: { ...chartOptions.plugins, title: { display: false } }
                                }}
                            />
                        </div>
                    </div>
                </div>

                {/* PDF PAGE 4: Evolution */}
                <div className="pdf-page w-full bg-white p-8 mt-4" style={{ minHeight: '297mm' }}>
                    <div className="mb-10">
                        <h3 className="text-2xl font-bold text-slate-800 text-center mb-6">Evolución de Actividad e Ingresos por Día</h3>
                        <div className="h-[500px] w-full">
                            <Line
                                data={dailyData}
                                options={{
                                    ...chartOptions,
                                    plugins: { ...chartOptions.plugins, title: { display: false } }
                                }}
                                plugins={[lineDataLabelsPlugin]}
                            />
                        </div>
                    </div>
                </div>

                {/* USER DETAILS TABLES (Paginated for PDF) */}
                {stats.user_details && stats.user_details.length > 0 && Array.from({ length: Math.ceil(stats.user_details.length / 35) }).map((_, pageIndex) => {
                    const chunk = stats.user_details.slice(pageIndex * 35, (pageIndex + 1) * 35);
                    return (
                        <div key={`user-table-${pageIndex}`} className="pdf-page w-full bg-white p-8 mt-4" style={{ minHeight: '297mm' }}>
                            <h3 className="text-2xl font-bold text-slate-800 text-center mb-6">
                                Detalle de Usuarios {stats.user_details.length > 35 ? `(Pág ${pageIndex + 1})` : ''}
                            </h3>
                            <table className="min-w-full bg-white border border-gray-200">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className="py-2 px-4 border-b font-semibold text-gray-700 text-left">Nombre</th>
                                        <th className="py-2 px-4 border-b font-semibold text-gray-700 text-left">Sala</th>
                                        <th className="py-2 px-4 border-b font-semibold text-gray-700 text-center">Ingreso</th>
                                        <th className="py-2 px-4 border-b font-semibold text-gray-700 text-center">Firma</th>
                                        <th className="py-2 px-4 border-b font-semibold text-gray-700 text-center">Voto</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {chunk.map((u, idx) => (
                                        <tr key={idx} className="hover:bg-gray-50">
                                            <td className="py-2 px-4 border-b text-sm text-gray-600">{u.name} {u.last_name}</td>
                                            <td className="py-2 px-4 border-b text-sm text-gray-600">{u.sala_name}</td>
                                            <td className="py-2 px-4 border-b text-center text-sm font-semibold">
                                                <span className={u.ingreso === 'Sí' ? 'text-green-600' : 'text-red-500'}>{u.ingreso}</span>
                                            </td>
                                            <td className="py-2 px-4 border-b text-center text-sm font-semibold">
                                                {u.signature ? (
                                                    <img src={u.signature} alt="Firma" className="h-8 object-contain mx-auto" />
                                                ) : (
                                                    <span className="text-red-500">No</span>
                                                )}
                                            </td>
                                            <td className="py-2 px-4 border-b text-center text-sm font-semibold">
                                                <span className={u.voto === 'Sí' ? 'text-green-600' : 'text-red-500'}>{u.voto}</span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    );
                })}

            </div>
        </div>
    );
};

export default Reports;
